import React from 'react';
import { Box, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { normalizeReminderTimes } from './medicationReminderScheduleAdapter';

const WEEKDAYS = [
  ['sun', 'Sun'], ['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'],
  ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'],
];

const MedicationReminderScheduleEditor = ({ schedule, onChange, onSave, saving }) => {
  const [timeError, setTimeError] = React.useState('');
  const set = (field, value) => onChange({ ...schedule, [field]: value });
  const timeText = (schedule.times || []).join(', ');
  const setTimes = (value) => set('times', value.split(',').map((item) => item.trim()).filter(Boolean));
  const toggleWeekday = (day) => set('weekdays', (schedule.weekdays || []).includes(day)
    ? schedule.weekdays.filter((item) => item !== day)
    : [...(schedule.weekdays || []), day]);
  const save = () => {
    const enteredTimes = schedule.times || [];
    const normalizedTimes = normalizeReminderTimes(enteredTimes);
    const hasInvalidTime = enteredTimes.some((time) => normalizeReminderTimes([time]).length === 0);
    if (!['', 'prn', 'interval_hours'].includes(schedule.kind) && (!normalizedTimes.length || hasInvalidTime)) {
      setTimeError('Enter a time like 12:00 or 12:00 PM.');
      return;
    }
    setTimeError('');
    onSave({ ...schedule, times: normalizedTimes });
  };

  return (
    <Stack spacing={1.2} sx={{ mt: 1.2 }}>
      <TextField select size="small" label="Schedule type" value={schedule.kind || ''} onChange={(event) => set('kind', event.target.value)}>
        <MenuItem value="">Needs confirmation</MenuItem>
        <MenuItem value="daily">Specific times each day</MenuItem>
        <MenuItem value="weekdays">Selected weekdays</MenuItem>
        <MenuItem value="interval_hours">Every few hours</MenuItem>
        <MenuItem value="interval_days">Every few days</MenuItem>
        <MenuItem value="weekly">Weekly</MenuItem>
        <MenuItem value="monthly">Monthly</MenuItem>
        <MenuItem value="prn">As needed (PRN)</MenuItem>
      </TextField>

      {!['', 'prn', 'interval_hours'].includes(schedule.kind) ? (
        <TextField
          size="small"
          label="Reminder times"
          value={timeText}
          placeholder="08:00, 20:00"
          error={Boolean(timeError)}
          helperText={timeError || 'Enter times separated by commas, such as 08:00, 20:00, or 12:00 PM.'}
          onChange={(event) => { setTimeError(''); setTimes(event.target.value); }}
        />
      ) : null}

      {schedule.kind === 'weekdays' ? (
        <Box role="group" aria-label="Reminder weekdays">
          {WEEKDAYS.map(([value, label]) => (
            <FormControlLabel
              key={value}
              control={<Checkbox size="small" checked={(schedule.weekdays || []).includes(value)} onChange={() => toggleWeekday(value)} />}
              label={label}
            />
          ))}
        </Box>
      ) : null}

      {schedule.kind === 'interval_hours' ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size="small"
            type="number"
            label="Hours between reminders"
            value={schedule.intervalHours || ''}
            helperText="Enter 8 for a reminder every 8 hours."
            onChange={(event) => set('intervalHours', event.target.value)}
            inputProps={{ min: 1 }}
          />
          <TextField size="small" type="time" label="First reminder time" value={schedule.anchorTime || ''} onChange={(event) => set('anchorTime', event.target.value)} InputLabelProps={{ shrink: true }} />
        </Stack>
      ) : null}

      {schedule.kind === 'interval_days' ? (
        <TextField
          size="small"
          type="number"
          label="Days between reminders"
          value={schedule.intervalDays || ''}
          helperText="Enter 2 for every other day."
          onChange={(event) => set('intervalDays', event.target.value)}
          inputProps={{ min: 1 }}
        />
      ) : null}

      {schedule.kind === 'monthly' ? (
        <TextField size="small" type="number" label="Day of month" value={schedule.dayOfMonth || ''} onChange={(event) => set('dayOfMonth', event.target.value)} inputProps={{ min: 1, max: 31 }} />
      ) : null}

      {!['', 'prn'].includes(schedule.kind) ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField size="small" type="date" label="Start date" value={schedule.startDate || ''} onChange={(event) => set('startDate', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField size="small" type="date" label="End date (optional)" value={schedule.endDate || ''} onChange={(event) => set('endDate', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        </Stack>
      ) : null}

      {schedule.kind === 'prn' ? (
        <Typography variant="body2">PRN medications do not create due or missed-dose occurrences.</Typography>
      ) : null}

      <Button variant="contained" onClick={save} disabled={saving || !schedule.kind} sx={{ minHeight: 44, alignSelf: 'flex-start' }}>
        {saving ? 'Saving…' : 'Save reminder schedule'}
      </Button>
    </Stack>
  );
};

export default MedicationReminderScheduleEditor;
